import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule } from '@ngx-translate/core';
import { isEmptyTranslationValue } from '@services/server-translate.service';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule, TranslateModule],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent implements ControlValueAccessor {
  @Input() type: string = 'text';
  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() size: 'small' | 'large' | undefined = undefined;
  @Input() variant: 'filled' | 'outlined' | undefined = undefined;
  @Input() step: string = '';
  @Input() min: number = 0;
  @Input() maxFractionDigits: number = 2;
  @Input() decimalSeparator: '.' | ',' | null = null;

  @Output() onFocus = new EventEmitter<FocusEvent>();
  @Output() onBlur = new EventEmitter<FocusEvent>();
  @Output() onInput = new EventEmitter<Event>();
  @Output() onDecimalSeparatorUsed = new EventEmitter<'.' | ','>();
  @Output() onSeparatorMismatch = new EventEmitter<'.' | ','>();

  value: string = '';

  protected onTouchedCallback: () => void = () => {};
  protected onChangeCallback: (_: any) => void = () => {};

  writeValue(value: any): void {
    if (this.isDecimalType() && this.isValidValue(value)) {
      this.value = this.formatDecimalValue(value);
    } else {
      this.value = this.normalizeInputValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onChange(value: string): void {
    this.value = value;
    this.onChangeCallback(value);
  }

  handleFocus(event: FocusEvent): void {
    this.onFocus.emit(event);
  }

  handleBlur(event: FocusEvent): void {
    this.onTouchedCallback();

    if (this.isDecimalType() && this.isValidValue(this.value)) {
      this.value = this.formatDecimalValue(this.value);
      this.onChangeCallback(this.value);
    }

    this.onBlur.emit(event);
  }

  handleInput(event: Event): void {
    if (this.isDecimalType()) {
      const input = event.target as HTMLInputElement;
      const sanitized = this.sanitizeDecimalValue(input.value);

      if (sanitized !== input.value) {
        input.value = sanitized;
      }

      this.value = sanitized;
      this.onChangeCallback(this.value);
    }

    this.onInput.emit(event);
  }

  handleKeydown(event: KeyboardEvent): void {
    if (this.isDecimalType()) {
      this.handleDecimalKeydown(event);
    }
  }

  private handleDecimalKeydown(event: KeyboardEvent): void {
    // Prevent negative sign and scientific notation
    if (event.key === '-' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
      return;
    }

    // Allow control keys
    if (this.isControlKey(event)) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    const key = event.key;

    // Handle decimal point/comma
    if (this.isDecimalSeparator(key)) {
      this.handleDecimalSeparator(event, currentValue);
      return;
    }

    // Allow only digits
    if (!this.isDigit(key)) {
      event.preventDefault();
      return;
    }

    // Enforce 2 decimal places limit
    this.enforceDecimalPlacesLimit(event, input, currentValue);
  }

  private isControlKey(event: KeyboardEvent): boolean {
    const key = event.key;
    return (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key.startsWith('Arrow') ||
      event.ctrlKey ||
      event.metaKey
    );
  }

  private isDecimalSeparator(key: string): boolean {
    return key === '.' || key === ',';
  }

  private handleDecimalSeparator(event: KeyboardEvent, currentValue: string): void {
    const hasDecimalPoint = currentValue.includes('.') || currentValue.includes(',');

    if (hasDecimalPoint) {
      event.preventDefault();
      return;
    }

    if (this.decimalSeparator) {
      if (event.key !== this.decimalSeparator) {
        event.preventDefault();
        this.onSeparatorMismatch.emit(this.decimalSeparator);
      }
    } else {
      this.onDecimalSeparatorUsed.emit(event.key as '.' | ',');
    }
  }

  private isDigit(key: string): boolean {
    return /^\d$/.test(key);
  }

  private enforceDecimalPlacesLimit(event: KeyboardEvent, input: HTMLInputElement, currentValue: string): void {
    const decimalIndex = Math.max(currentValue.indexOf('.'), currentValue.indexOf(','));

    if (decimalIndex === -1) {
      return;
    }

    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const selectionLength = selectionEnd - selectionStart;
    const decimalPart = currentValue.substring(decimalIndex + 1);

    if (selectionStart > decimalIndex) {
      const projectedLength = decimalPart.length - selectionLength + 1;

      if (projectedLength > this.maxFractionDigits) {
        event.preventDefault();
      }
    }
  }

  private sanitizeDecimalValue(rawValue: string): string {
    if (!rawValue) {
      return '';
    }

    const normalized = rawValue
      .replace(',', '.')
      .replace(/[^0-9.]/g, '');

    const [integerPart = '', decimalPart = ''] = normalized.split('.', 2);
    const trimmedDecimals = decimalPart.slice(0, this.maxFractionDigits);

    if (trimmedDecimals.length > 0) {
      return `${integerPart || '0'}.${trimmedDecimals}`;
    }

    return integerPart || '';
  }

  private isDecimalType(): boolean {
    return this.type === 'double' || this.type === 'number';
  }

  private isValidValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  private normalizeInputValue(value: any): string {
    if (isEmptyTranslationValue(value)) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  private formatDecimalValue(value: any): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return value || '';
    }

    return Number(numValue.toFixed(this.maxFractionDigits)).toString();
  }
}
